import { getSignal, VehicleFuelType } from '@api/index';
import { getFeatureToggles, getPerformVehicles } from '@common/permissions/selectors';
import Box from '@components/Box';
import OperatingPeriod from '@components/OperatingPeriod';
import VehiclesInfoTabs from '@components/VehiclesInfoTabs';
import { Loadable } from '@data/loadable';
import OperationalDays from '@features/operationalDays';
import PDFDownloader from '@features/pdf/detail/DriverPDFDownloader';
import {
    driverSidebarData,
    type EnrichedSidebarData,
    hideSidebar,
    isEnrichedSidebarData,
    showSidebar,
    type SidebarData,
} from '@features/sidebar';
import { getActiveDateRange } from '@features/ui/selectors';
import Sidebar from '@rio-cloud/rio-uikit/Sidebar';
import {
    combustionColumnNames,
    commonColumnNames,
    electricColumnNames,
    getColumns,
    hydrateColumns,
} from '@utils/columns';
import { convertToRows } from '@utils/convertToRows';
import { extractDriverName } from '@utils/stringFormatters';
import { trackingAttributes } from '@utils/tracking';
import moment from 'moment';
import React, { useCallback, useMemo, useState } from 'react';
import { FormattedDate, FormattedMessage } from 'react-intl';
import { connect } from 'react-redux';
import { Dispatch } from 'redux';
import { createSelector } from 'reselect';

import { Column as ColumnType } from '../../columns/createColumn';
import { DRIVER_ROUTE } from '../../constants/routes';
import { State } from '../../setup/types';
import {
    DateRange,
    Driver,
    HydratedEntity,
    HydratedEntityWithChildren,
    Vehicle,
} from '../../types';
import { getTopColumns } from './columns';
import { openRow } from './driverAnalysisReducer';
import Column from './DriverColumn';
import Tabs from './DriverSidebarTabs';
import useDriverContentLoader from './hooks/useDriverContentLoader';
import { Data } from './types';

const VEHICLE_TAB_ICON = 'rioglyph rioglyph-tachograph';
const SIDEBAR_WIDTH = 1000;
const ROW_COLUMNS_COUNT = 3;

const convertToRow = convertToRows(ROW_COLUMNS_COUNT);

export enum TabType {
    FUEL_TAB = 'fuelVehicles',
    ELECTRIC_TAB = 'electricVehicles',
}

type DriverSidebarContentProps = {
    data: EnrichedSidebarData;
    onClose: () => void;
    dateRange: DateRange;
    onVehicleClick: (vehicle: Data) => void;
    openParent: (id?: string) => void;
    allowedVehicles: string[];
    shouldShowOvertakeInCruise?: boolean;
    shouldUseV3Ratings: boolean;
};

type DriverSidebarStateProps = {
    data: EnrichedSidebarData | undefined;
    dateRange: DateRange;
    allowedVehicles: string[];
    shouldShowOvertakeInCruise: boolean;
    shouldUseV3Ratings: boolean;
};

type ChildrenData = {
    electric: HydratedEntity[];
    fuel: HydratedEntity[];
};

const getInitialTab = (data: EnrichedSidebarData): TabType =>
    data.containsElectricVehicle && !data.containsCombustionVehicle
        ? TabType.ELECTRIC_TAB
        : TabType.FUEL_TAB;

const getAdjustedEndDate = (date: Date): Date => {
    const isStartOfDay = date.getHours() === 0 && date.getMinutes() === 0;
    return isStartOfDay ? moment(date).subtract(1, 'minutes').toDate() : date;
};

const filterAllowedVehicles = (
    vehicles: Vehicle[] | undefined,
    allowedVehicles: string[],
): string[] => {
    if (!vehicles) return [];
    return vehicles.filter((v) => allowedVehicles.includes(v.id)).map((v) => v.id);
};

const filterChildrenByFuelType = (children: HydratedEntity[]): ChildrenData => {
    const isAllElectric = (child: HydratedEntity) =>
        child.vehicles?.every((vehicle) => vehicle.fuelType === VehicleFuelType.ELECTRIC) ?? false;

    const isAllFuel = (child: HydratedEntity) =>
        child.vehicles?.every((vehicle) => vehicle.fuelType !== VehicleFuelType.ELECTRIC) ?? false;

    return {
        electric: children.filter(isAllElectric),
        fuel: children.filter(isAllFuel),
    };
};

const OperatingDataHeader: React.FC = React.memo(() => (
    <div className="margin-top-10 display-flex text-size-18 flex-row align-items-center">
        <span className="margin-right-10">
            <span className={VEHICLE_TAB_ICON} />
        </span>
        <span className="text-size-18">
            <FormattedMessage id="operatingData.label" />
        </span>
    </div>
));
OperatingDataHeader.displayName = 'OperatingDataHeader';

export const FormattedOperationDays: React.FC<{ entity: HydratedEntity }> = React.memo(
    ({ entity }) => (
        <div className="margin-bottom-5">
            <label className="margin-bottom-0 text-size-12">
                <FormattedMessage id="operationDays" />
            </label>
            <div className="text-size-18 ellipsis-1">
                <OperationalDays
                    operatingDays={getSignal(entity, 'operatingDays', undefined)}
                    totalNumberOfDays={getSignal(entity, 'totalNumberOfDays', undefined)}
                />
            </div>
        </div>
    ),
);
FormattedOperationDays.displayName = 'FormattedOperationDays';

export const DriverSidebar: React.FC<DriverSidebarContentProps> = React.memo(
    ({
        data,
        onClose,
        dateRange,
        onVehicleClick,
        openParent,
        allowedVehicles,
        shouldShowOvertakeInCruise = false,
        shouldUseV3Ratings = false,
    }) => {
        const [tabKey, setTabKey] = useState<TabType>(() => getInitialTab(data));

        const currentData = useMemo(
            () =>
                tabKey === TabType.ELECTRIC_TAB
                    ? { vehicleIds: data.electricVehicleIds, vehicles: data.electricVehicles }
                    : { vehicleIds: data.fuelVehicleIds, vehicles: data.fuelVehicles },
            [
                tabKey,
                data.electricVehicleIds,
                data.electricVehicles,
                data.fuelVehicleIds,
                data.fuelVehicles,
            ],
        );

        const childrenData = useMemo(
            () => filterChildrenByFuelType(data.children ?? []),
            [data.children],
        );

        const currentChildren = useMemo(
            () => (tabKey === TabType.ELECTRIC_TAB ? childrenData.electric : childrenData.fuel),
            [tabKey, childrenData],
        );

        const loaderData = useDriverContentLoader({
            selectedDriverIds: data.driverIds ?? [],
            dateRange,
            shouldShowHarshKPIs: false,
            shouldUseV2Ratings: false,
            shouldShowExcessiveKPIs: false,
            shouldUseV3Ratings,
            vehicles: currentData.vehicleIds,
        });

        const loadedAttributes = useMemo(
            () =>
                Loadable.cata(
                    loaderData.attributes,
                    (d) => d,
                    () => data.attributes ?? [],
                    () => data.attributes ?? [],
                    () => data.attributes ?? [],
                ),
            [loaderData.attributes, data.attributes],
        );

        const tabEntity = useMemo(
            () =>
                Loadable.cata(
                    loaderData.parentsWithChildren,
                    (loadedData) => loadedData[0],
                    () => [],
                    () => [],
                    () => undefined,
                ) as EnrichedSidebarData | undefined,
            [loaderData.parentsWithChildren],
        );

        const tabEntityEnriched = useMemo(() => {
            if (!tabEntity) return undefined;
            return {
                ...tabEntity,
                drivers: data.drivers,
                vehicles: currentData.vehicles,
                children: currentChildren,
                childrenCount: currentChildren.length,
                attributes: loadedAttributes,
            };
        }, [tabEntity, data.drivers, currentData.vehicles, currentChildren]);

        const onVehiclesNameClick = useCallback(
            (vehicle: Data) => {
                onVehicleClick(vehicle);
                openParent(data.id);
            },
            [onVehicleClick, openParent, data.id],
        );

        const isVehiclesNameClickable = data.isVehiclesNameClickable ?? false;

        const topColumns = useMemo(
            () =>
                hydrateColumns(
                    getTopColumns(
                        isVehiclesNameClickable,
                        onVehiclesNameClick as (vehicle: unknown) => void,
                    ).filter((c): c is ColumnType => !!c),
                    loadedAttributes,
                ),
            [isVehiclesNameClickable, onVehiclesNameClick, loadedAttributes],
        );

        const topRows = useMemo(
            () =>
                convertToRow(
                    topColumns.map((column) => (
                        <Column key={column?.key} column={column} entity={data} />
                    )),
                ),
            [topColumns, data],
        );

        const commonColumns = useMemo(
            () => hydrateColumns(getColumns(commonColumnNames), loadedAttributes),
            [loadedAttributes],
        );
        const combustionColumns = useMemo(
            () =>
                hydrateColumns(getColumns(combustionColumnNames), loadedAttributes).concat(
                    commonColumns,
                ),
            [commonColumns, loadedAttributes],
        );
        const electricColumns = useMemo(
            () =>
                hydrateColumns(getColumns(electricColumnNames), loadedAttributes).concat(
                    commonColumns,
                ),
            [commonColumns, loadedAttributes],
        );

        const displayedCombustionVehicleColumns = useMemo(
            () =>
                combustionColumns
                    .map((column: ColumnType) => (
                        <Column key={column.key} column={column} entity={data} />
                    ))
                    .concat([
                        <FormattedOperationDays
                            key="operationDays"
                            entity={data as HydratedEntityWithChildren}
                        />,
                    ]),
            [combustionColumns, data],
        );

        const displayedElectricVehicleColumns = useMemo(
            () =>
                electricColumns
                    .map((column: ColumnType) => (
                        <Column key={column.key} column={column} entity={data} />
                    ))
                    .concat([
                        <FormattedOperationDays
                            key="operationDays"
                            entity={data as HydratedEntityWithChildren}
                        />,
                    ]),
            [electricColumns, data],
        );

        const vehicleCombustionRows = useMemo(
            () => convertToRow(displayedCombustionVehicleColumns),
            [displayedCombustionVehicleColumns],
        );

        const vehicleElectricRows = useMemo(
            () => convertToRow(displayedElectricVehicleColumns),
            [displayedElectricVehicleColumns],
        );

        const containsElectricVehicle = data.containsElectricVehicle ?? false;
        const containsCombustionVehicle = data.containsCombustionVehicle ?? false;
        const driver = useMemo(
            () => extractDriverName(data.drivers?.[0] ?? ({} as Driver)),
            [data.drivers],
        );
        const startDate = dateRange.start;
        const endDate = useMemo(() => getAdjustedEndDate(dateRange.end), [dateRange.end]);
        const entityForTabs = useMemo(
            () => (data.vehicles?.length === 1 ? data : (tabEntityEnriched ?? tabEntity)) ?? data,
            [data, tabEntityEnriched, tabEntity],
        );
        const filteredAllowedVehicles = useMemo(
            () => filterAllowedVehicles(data.vehicles, allowedVehicles),
            [data.vehicles, allowedVehicles],
        );

        if (!tabEntity && data.vehicles?.length !== 1) {
            return null;
        }

        return (
            <Sidebar
                fly
                onClose={onClose}
                position="right"
                width={SIDEBAR_WIDTH}
                title={
                    <div
                        className="padding-left-10 justify-content-between flex align-items-center"
                        data-test="DriverSidebar"
                        {...trackingAttributes(
                            'visibility',
                            'perform, detailsPanel',
                            'openDriverDetailsPanel',
                            'openDetailsPanel, drivingAnalysis',
                        )}
                    >
                        <FormattedMessage
                            id="driverAnalysis.sidebar.title.oneDriver"
                            values={{
                                driver,
                                start: <FormattedDate value={startDate} />,
                                end: <FormattedDate value={endDate} />,
                            }}
                        />
                    </div>
                }
                headerButtons={
                    <PDFDownloader
                        entity={data as HydratedEntityWithChildren}
                        dateRange={dateRange}
                        shouldUseV3Ratings={shouldUseV3Ratings}
                        attributes={loadedAttributes}
                    />
                }
                bodyClassName="overflow-y-scroll"
            >
                <OperatingPeriod
                    start={
                        getSignal<Date | string | undefined>(data, 'start', undefined) as
                            Date | string
                    }
                    end={
                        getSignal<Date | string | undefined>(data, 'end', undefined) as
                            Date | string
                    }
                />
                <div className="padding-left-20 padding-right-20 padding-top-15 padding-bottom-15">
                    <Box rows={topRows} hasBorder={false} />
                    {containsElectricVehicle && containsCombustionVehicle && tabEntityEnriched && (
                        <VehiclesInfoTabs
                            dateRange={dateRange}
                            shouldUseV3Ratings={shouldUseV3Ratings}
                            shouldUseV2Ratings={false}
                            shouldShowHarshKPIs={false}
                            shouldShowExcessiveKPIs={false}
                            tabKey={tabKey}
                            setTabKey={setTabKey}
                            data={tabEntityEnriched}
                            attributes={loadedAttributes}
                        />
                    )}
                    {containsElectricVehicle && !containsCombustionVehicle && (
                        <>
                            <OperatingDataHeader />
                            <Box rows={vehicleElectricRows} hasBorder={false} />
                        </>
                    )}
                    {!containsElectricVehicle && containsCombustionVehicle && (
                        <>
                            <OperatingDataHeader />
                            <Box rows={vehicleCombustionRows} hasBorder={false} />
                        </>
                    )}

                    <Tabs
                        tabKey={tabKey}
                        dateRange={dateRange}
                        entity={entityForTabs}
                        allowedVehicles={filteredAllowedVehicles}
                        shouldShowOvertakeInCruise={shouldShowOvertakeInCruise}
                        containsElectricVehicle={containsElectricVehicle}
                        attributes={loadedAttributes}
                    />
                </div>
            </Sidebar>
        );
    },
);
DriverSidebar.displayName = 'DriverSidebar';

const getDriverSidebarData = (state: State) => driverSidebarData(state);
const getDriverSidebarDateRange = (state: State) => getActiveDateRange(state);
const getDriverSidebarAllowedVehicles = (state: State) => getPerformVehicles(state);
const getDriverSidebarShowOvertake = (state: State) =>
    getFeatureToggles(state).showOvertakeInCruise as boolean;
const getDriverSidebarV3Ratings = (state: State) =>
    getFeatureToggles(state).newOvertakeModel as boolean;

export const mapStateToProps = createSelector(
    [
        getDriverSidebarData,
        getDriverSidebarDateRange,
        getDriverSidebarAllowedVehicles,
        getDriverSidebarShowOvertake,
        getDriverSidebarV3Ratings,
    ],
    (
        data,
        dateRange,
        allowedVehicles,
        shouldShowOvertakeInCruise,
        shouldUseV3Ratings,
    ): DriverSidebarStateProps => {
        if (!data || !isEnrichedSidebarData(data)) {
            return {
                data: undefined,
                dateRange,
                allowedVehicles,
                shouldShowOvertakeInCruise,
                shouldUseV3Ratings,
            };
        }

        return {
            data,
            dateRange,
            allowedVehicles,
            shouldShowOvertakeInCruise,
            shouldUseV3Ratings,
        };
    },
);

const mapDispatchToProps = (dispatch: Dispatch) => ({
    onVehicleClick: (vehicleData: Data) =>
        dispatch(
            showSidebar({
                data: {
                    ...vehicleData,
                    childrenIds: vehicleData.children?.map((c) => c.id) ?? [],
                } as unknown as SidebarData,
                type: DRIVER_ROUTE,
            }),
        ),
    openParent: (id?: string) => {
        if (id) {
            dispatch(openRow({ id }));
        }
    },
    onClose: () => dispatch(hideSidebar()),
});

const ConnectedDriverSidebar = connect(
    mapStateToProps,
    mapDispatchToProps,
)((props: DriverSidebarStateProps & ReturnType<typeof mapDispatchToProps>) => {
    const { data, ...rest } = props;
    if (!data) return null;
    return <DriverSidebar {...rest} data={data} />;
});

export default ConnectedDriverSidebar;
